/**
 * Billing API service — subscription management, checkout, portal, invoices
 */
import { apiFetch } from '@/lib/api-client'

const BILLING_BASE = '/billing'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type PlanType = 'free' | 'pro'
export type BillingCycle = 'monthly' | 'yearly'
export type SubscriptionStatus =
  | 'active'
  | 'trialing'
  | 'past_due'
  | 'canceled'
  | 'incomplete'
  | 'unpaid'
  | 'paused'

export interface UsageInfo {
  current: number
  max: number
  remaining: number
}

export type PaymentIssueKind = 'payment_failed' | 'card_expiring'

/** Sitede yan menü altındaki ödeme uyarı kartının verisi (erişim anahtarı kapalıysa null). */
export interface PaymentIssue {
  kind: PaymentIssueKind
  deadline: string | null
}

export interface SubscriptionData {
  plan_type: PlanType
  status: SubscriptionStatus | null
  billing_cycle: BillingCycle | null
  current_period_end: string | null
  cancel_at_period_end: boolean
  cancel_at?: string | null
  source?: SubscriptionSource
  trial_ends_at?: string | null
  paused?: boolean
  trial_expired?: boolean
  grace_period_until?: string | null
  grace_period_expired?: boolean
  gift_expired?: boolean
  /** Eski backend bu alanı göndermez (undefined) */
  payment_issue?: PaymentIssue | null
  limits: Record<string, number>
  features: Record<string, boolean>
  usage: Record<string, UsageInfo>
}

export interface InvoiceData {
  id: number
  stripe_invoice_id: string
  amount_paid: number
  amount_due: number
  currency: string
  status: string
  invoice_url: string | null
  invoice_pdf: string | null
  period_start: string | null
  period_end: string | null
  paid_at: string | null
  created_at: string
}

export interface PlanLimitData {
  plan_type: string
  max_asin_tracking: number
  max_keywords: number
  max_deepsearch_per_month: number
  max_api_calls_per_day: number
  features: Record<string, boolean>
}

export type SubscriptionSource = 'stripe' | 'admin_gift' | 'trial'

export interface AdminSubscriptionData {
  id: number
  stripe_subscription_id: string
  plan_type: PlanType
  billing_cycle: BillingCycle
  status: SubscriptionStatus
  source: SubscriptionSource
  cancel_at_period_end: boolean
  cancel_at: string | null
  paused_at: string | null
  trial_ends_at: string | null
  granted_by: string | null
  current_period_end: string | null
  customer_id: number
  email?: string
  is_test_data?: boolean
  created_at: string
}

// ---------------------------------------------------------------------------
// User endpoints
// ---------------------------------------------------------------------------

export async function getSubscription(): Promise<SubscriptionData> {
  const res = await apiFetch<{ success: boolean; data: SubscriptionData }>(
    `${BILLING_BASE}/subscription`
  )
  return res.data
}

/** Ödeme onayı (#842): anahtar açıkken gövdeye eklenen alanlar. */
export interface CheckoutConsent {
  terms_accepted: true
  terms_version: string
  consent_text_version: string
}

export async function createCheckoutSession(
  billingCycle: BillingCycle,
  consent?: CheckoutConsent,
): Promise<{ checkout_url: string; session_id: string }> {
  const res = await apiFetch<{
    success: boolean
    data: { checkout_url: string; session_id: string }
  }>(`${BILLING_BASE}/checkout`, {
    method: 'POST',
    body: JSON.stringify({ billing_cycle: billingCycle, ...(consent ?? {}) }),
  })
  return res.data
}

export async function createPortalSession(): Promise<{ portal_url: string }> {
  const res = await apiFetch<{
    success: boolean
    data: { portal_url: string }
  }>(`${BILLING_BASE}/portal`, { method: 'POST' })
  return res.data
}

export async function getInvoices(
  page = 1,
  perPage = 20
): Promise<{ invoices: InvoiceData[]; total: number }> {
  const res = await apiFetch<{
    success: boolean
    data: { invoices: InvoiceData[]; total: number }
  }>(`${BILLING_BASE}/invoices?page=${page}&per_page=${perPage}`)
  return res.data
}

export async function getUsage(): Promise<{
  plan_type: PlanType
  limits: Record<string, number>
  usage: Record<string, UsageInfo>
}> {
  const res = await apiFetch<{
    success: boolean
    data: { plan_type: PlanType; limits: Record<string, number>; usage: Record<string, UsageInfo> }
  }>(`${BILLING_BASE}/usage`)
  return res.data
}

// Backend /billing/confirm Pro yapılınca SubscriptionData, ödeme henüz tamamlanmadıysa
// (D-17) { status: 'pending' } döner — discriminated union ile modellenir ki çağıran
// (billing.tsx) pending şeklinde plan_type/billing_cycle'ı körlemesine okumasın.
export type ConfirmCheckoutResult = SubscriptionData | { status: 'pending' }

export async function confirmCheckout(sessionId: string): Promise<ConfirmCheckoutResult> {
  const res = await apiFetch<{ success: boolean; data: ConfirmCheckoutResult }>(
    `${BILLING_BASE}/confirm`,
    {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    }
  )
  return res.data
}

export async function cancelSubscription(): Promise<void> {
  await apiFetch(`${BILLING_BASE}/cancel`, { method: 'POST' })
}

export async function reactivateSubscription(): Promise<void> {
  await apiFetch(`${BILLING_BASE}/reactivate`, { method: 'POST' })
}

export async function getBillingConfig(): Promise<{
  publishable_key: string
  prices: { pro_monthly: string; pro_yearly: string }
}> {
  const res = await apiFetch<{
    success: boolean
    data: { publishable_key: string; prices: { pro_monthly: string; pro_yearly: string } }
  }>(`${BILLING_BASE}/config`)
  return res.data
}

// ---------------------------------------------------------------------------
// Admin endpoints
// ---------------------------------------------------------------------------

export async function adminListSubscriptions(params?: {
  page?: number
  per_page?: number
  plan_type?: string
  status?: string
}): Promise<{ subscriptions: AdminSubscriptionData[]; total: number }> {
  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set('page', String(params.page))
  if (params?.per_page) searchParams.set('per_page', String(params.per_page))
  if (params?.plan_type) searchParams.set('plan_type', params.plan_type)
  if (params?.status) searchParams.set('status', params.status)

  const res = await apiFetch<{
    success: boolean
    data: { subscriptions: AdminSubscriptionData[]; total: number }
  }>(`${BILLING_BASE}/admin/subscriptions?${searchParams}`)
  return res.data
}

export async function adminGetRevenue(): Promise<{
  total_subscriptions: number
  active: number
  canceled: number
  past_due: number
  churn_rate: number
}> {
  const res = await apiFetch<{ success: boolean; data: {
    total_subscriptions: number
    active: number
    canceled: number
    past_due: number
    churn_rate: number
  } }>(
    `${BILLING_BASE}/admin/revenue`
  )
  return res.data
}

export async function adminGetPlanLimits(): Promise<PlanLimitData[]> {
  const res = await apiFetch<{ success: boolean; data: PlanLimitData[] }>(
    `${BILLING_BASE}/admin/plan-limits`
  )
  return res.data
}

export async function adminUpdatePlanLimits(
  planType: string,
  updates: Partial<PlanLimitData>
): Promise<PlanLimitData> {
  const res = await apiFetch<{ success: boolean; data: PlanLimitData }>(
    `${BILLING_BASE}/admin/plan-limits/${planType}`,
    { method: 'PUT', body: JSON.stringify(updates) }
  )
  return res.data
}

export async function adminGrantSubscription(params: {
  user_email: string
  plan_type?: string
  duration_days?: number | null
  notes?: string
}): Promise<AdminSubscriptionData> {
  const res = await apiFetch<{ success: boolean; data: AdminSubscriptionData }>(
    `${BILLING_BASE}/admin/grant`,
    { method: 'POST', body: JSON.stringify(params) }
  )
  return res.data
}

export async function adminCreateTrial(params: {
  user_email: string
  trial_days?: number
  custom_limits?: Record<string, number>
}): Promise<AdminSubscriptionData> {
  const res = await apiFetch<{ success: boolean; data: AdminSubscriptionData }>(
    `${BILLING_BASE}/admin/trial`,
    { method: 'POST', body: JSON.stringify(params) }
  )
  return res.data
}

export async function adminPauseSubscription(subscriptionId: number): Promise<AdminSubscriptionData> {
  const res = await apiFetch<{ success: boolean; data: AdminSubscriptionData }>(
    `${BILLING_BASE}/admin/subscriptions/${subscriptionId}/pause`,
    { method: 'PUT' }
  )
  return res.data
}

export async function adminUnpauseSubscription(subscriptionId: number): Promise<AdminSubscriptionData> {
  const res = await apiFetch<{ success: boolean; data: AdminSubscriptionData }>(
    `${BILLING_BASE}/admin/subscriptions/${subscriptionId}/unpause`,
    { method: 'PUT' }
  )
  return res.data
}

export async function adminUpdateCustomerLimits(
  customerId: number,
  customLimits: Record<string, number> | null
): Promise<{ customer_id: number; custom_limits: Record<string, number> | null }> {
  const res = await apiFetch<{
    success: boolean
    data: { customer_id: number; custom_limits: Record<string, number> | null }
  }>(
    `${BILLING_BASE}/admin/customers/${customerId}/limits`,
    { method: 'PUT', body: JSON.stringify({ custom_limits: customLimits }) }
  )
  return res.data
}

export async function adminDeleteSubscription(subscriptionId: number): Promise<void> {
  await apiFetch(`${BILLING_BASE}/admin/subscriptions/${subscriptionId}`, { method: 'DELETE' })
}

export interface AuditLogEntry {
  id: number
  admin_user_id: string
  action: string
  target_customer_id: number | null
  target_subscription_id: number | null
  details: Record<string, unknown>
  created_at: string
}

// User overview (side panel data)
export interface CustomerOverviewData {
  id: number
  supabase_user_id: string
  stripe_customer_id: string | null
  email: string
  name: string | null
  custom_limits: Record<string, number> | null
  notes: string | null
  created_at: string
}

export interface UserOverviewResponse {
  customer: CustomerOverviewData
  subscriptions: AdminSubscriptionData[]
  invoices: { items: InvoiceData[]; total: number; page: number; per_page: number }
  audit_logs: { items: AuditLogEntry[]; total: number; page: number; per_page: number }
}

export async function adminGetUserOverview(
  supabaseUserId: string,
  params?: { invoice_page?: number; audit_page?: number }
): Promise<UserOverviewResponse> {
  const searchParams = new URLSearchParams()
  if (params?.invoice_page) searchParams.set('invoice_page', String(params.invoice_page))
  if (params?.audit_page) searchParams.set('audit_page', String(params.audit_page))

  const res = await apiFetch<{ success: boolean; data: UserOverviewResponse }>(
    `${BILLING_BASE}/admin/users/${supabaseUserId}/overview?${searchParams}`
  )
  return res.data
}

export async function adminGetAuditLog(params?: {
  page?: number
  per_page?: number
  action?: string
}): Promise<{ logs: AuditLogEntry[]; total: number }> {
  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set('page', String(params.page))
  if (params?.per_page) searchParams.set('per_page', String(params.per_page))
  if (params?.action) searchParams.set('action', params.action)

  const res = await apiFetch<{
    success: boolean
    data: { logs: AuditLogEntry[]; total: number }
  }>(`${BILLING_BASE}/admin/audit-log?${searchParams}`)
  return res.data
}

// ---------------------------------------------------------------------------
// Admin: Stripe Sync & Dead-Letter
// ---------------------------------------------------------------------------

export async function adminSyncUser(supabaseUserId: string): Promise<{
  user_id: string
  mismatches_found: number
  mismatches_fixed: number
  details: string
  error?: string
}> {
  const res = await apiFetch<{
    success: boolean
    data: {
      user_id: string
      mismatches_found: number
      mismatches_fixed: number
      details: string
      error?: string
    }
  }>(`${BILLING_BASE}/admin/sync/${supabaseUserId}`, { method: 'POST' })
  return res.data
}

export interface DeadLetterEvent {
  id: number
  stripe_event_id: string
  event_type: string
  error_message: string | null
  created_at: string | null
  attempts: number
  last_attempted_at: string | null
}

export async function adminListDeadLetters(params?: {
  page?: number
  per_page?: number
}): Promise<{ events: DeadLetterEvent[]; total: number; page: number; per_page: number }> {
  const searchParams = new URLSearchParams()
  if (params?.page) searchParams.set('page', String(params.page))
  if (params?.per_page) searchParams.set('per_page', String(params.per_page))

  // NOTE: This endpoint returns events at top level, not under data key
  const res = await apiFetch<{
    success: boolean
    events: DeadLetterEvent[]
    total: number
    page: number
    per_page: number
  }>(`${BILLING_BASE}/webhooks/admin/dead-letter?${searchParams}`)
  return { events: res.events, total: res.total, page: res.page, per_page: res.per_page }
}

export async function adminReprocessEvent(eventId: number): Promise<void> {
  await apiFetch(`${BILLING_BASE}/webhooks/admin/reprocess/${eventId}`, { method: 'POST' })
}
